import React, { useState } from "react"
import { withPrefix } from "gatsby"
import Layout from "../../components/layout"
import SubNavigation from "../../components/sub-navigation"
import Hero from "../../components/hero"
import ReadNext from "../../components/read-next"
import boardActionsData from "../../data/board-actions-data.json"
import boardActionsFilters from "../../data/board-actions-filters.json"
import { slugify, capitalizeFirstLetter } from "../../helpers/strings"

const PAGESLUG = "economic-surveillance"

export default function EconomicSurveillance() {
  const [actionsFilter, setActionsFilter] = useState("all") // Accepts a filter slug

  // Process and store the board actions data *once* into datasets based on the filters
  var processedData = []

  boardActionsFilters.forEach(filter => {
    if (filter.slug) {
      if (filter.slug === "all") {
        processedData[filter.slug] = boardActionsData
      } else {
        processedData[filter.slug] = boardActionsData.filter(action =>
          action.categories.includes(filter.slug)
        )
      }
    }
  })

  // Update the current dataset according to the actionsFilter state
  var filteredData = processedData[actionsFilter]

  //
  // Event Handlers
  //

  function handleSelectChange(e) {
    setActionsFilter(e.target.value)
  }

  return (
    <Layout>
      <SubNavigation id="What We Do" current={PAGESLUG} />
      <Hero
        image="what-we-do/economic-surveillance.jpg"
        title="Economic Surveillance"
        text="Through surveillance, the IMF monitors the international monetary system and global economic developments, and engages in a health check of the economic and financial policies of its 190 member countries. In addition, the IMF highlights possible stability risks to its member countries and advises their governments on potential policy adjustments, enabling the international monetary system to achieve its goal of facilitating the exchange of goods, services, and capital among countries, thereby sustaining sound economic growth."
        widthLarge="true"
      />
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--large">
            <h2 className="page__title">Introduction</h2>
            <p>
              The Board discusses all aspects of the Fund’s work, from the IMF
              staff’s annual health checks of member countries’ economies to
              policy issues relevant to the global economy. The Board carries
              out its work largely on the basis of papers prepared by IMF
              management and staff. In FY 2020, the IMF conducted 129 Article IV
              consultations (see{" "}
              <a href={withPrefix("/") + "downloads/web-tables.pdf"}>
                Web Table 2.1
              </a>
              ) and published externally 59 Policy Papers.
            </p>
            <p>
              The IMF also issues biannual reports and updates on the latest
              global economic developments—the <i>World Economic Outlook</i>,
              the <i>Global Financial Stability Report</i>, the{" "}
              <i>Fiscal Monitor</i>, and the <i>External Sector Report</i>—that
              provide important economic forecasting and policy guidance.
            </p>
          </div>
        </div>
      </section>
      <section className="page__section page__section--border-top">
        <div className="page__section__inner">
          <div className="container container--large">
            <div id="board-actions" className="board-actions">
              <header>
                <h2 className="page__title">Policy Papers</h2>
                <div className="board-actions__filter-wrapper">
                  <header
                    id="board-actions__filter__header"
                    className="title--micro title--light title--uppercase"
                  >
                    Filter By
                  </header>
                  <select
                    name="filters"
                    id="filters-select"
                    className="board-actions__filter"
                    value={actionsFilter}
                    onChange={handleSelectChange}
                    aria-labelledby="board-actions__filter__header"
                    aria-label="Filter the board actions"
                    aria-controls="board-actions"
                  >
                    {boardActionsFilters.map((filter, index) => {
                      return (
                        <option
                          key={"filter-select-" + filter.slug + index}
                          value={filter.slug}
                        >
                          {filter.title}
                        </option>
                      )
                    })}
                  </select>
                </div>
              </header>
              <div className="board-actions__items">
                {filteredData.map((action, index) => {
                  // Action id must be totally unique or the list will not render/filter correctly
                  let id = `board-actions-item-${slugify(
                    action.title
                  )}-${slugify(action.date)}-${
                    action.series_number && slugify(action.series_number)
                  }-${index}`

                  return <BoardActionItem key={id} id={id} action={action} />
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      <ReadNext sectionID="what-we-do" pageID="lending" />
    </Layout>
  )
}

//
// Subcomponents
//

function BoardActionItem({ id, action }) {
  const [textIsTruncated, setTextIsTruncated] = useState(true)

  var truncatedText = action.summary && truncateText(action.summary)

  function truncateText(str) {
    return str.split(" ").splice(0, 25).join(" ")
  }

  function formatDate(dateString) {
    const months = {
      jan: "January",
      feb: "February",
      mar: "March",
      apr: "April",
      may: "May",
      jun: "June",
      jul: "July",
      aug: "August",
      sep: "September",
      oct: "October",
      nov: "November",
      dec: "December",
    }

    let dateStringArray = dateString.split("-")

    return `${months[dateStringArray[1].toLowerCase()]} ${
      dateStringArray[0]
    }, 20${dateStringArray[2]}`
  }

  function formatCategories(categoriesString) {
    let categories = categoriesString.split(", ")

    categories.forEach((category, i) => {
      let words = category.split("-")

      words.forEach((word, i) => {
        words[i] = capitalizeFirstLetter(word)
      })

      categories[i] = words.join(" ").replace("Covid 19", "COVID-19")
    })

    return categories.join(", ")
  }

  function handleReadMoreButtonClick() {
    setTextIsTruncated(false)
  }

  return (
    <div id={id} className="board-actions__items__item">
      <div className="board-actions__items__item__date">
        <p className="title--micro title--light title--uppercase">
          {formatDate(action.date)}
        </p>
        <div className="board-actions__items__item__date__line"></div>
      </div>
      <div className="board-actions__items__item__content">
        <header>
          <p className="title--micro title--light title--uppercase">
            {formatCategories(action.categories)}
          </p>
          <h3 className="title--tertiary title--bold">
            {action.link ? (
              <a href={action.link} target="_blank" rel="noreferrer">
                {action.title}
              </a>
            ) : (
              action.title
            )}
          </h3>
        </header>

        {action.summary?.length &&
          (truncatedText?.length &&
          action.summary?.length > truncatedText?.length ? (
            <>
              <p className={textIsTruncated ? "" : "util__hidden"}>
                {truncatedText}…{" "}
                <button
                  className="board-actions__items__item__content__read-more-button"
                  type="button"
                  onClick={handleReadMoreButtonClick}
                  aria-controls={id}
                >
                  Read More
                </button>
              </p>
              <p className={textIsTruncated ? "util__hidden" : ""}>
                {action.summary}
              </p>
              {action.press_release && (
                <p className={textIsTruncated ? "util__hidden" : ""}>
                  For more information, read the{" "}
                  <a
                    href={action.press_release}
                    target="_blank"
                    rel="noreferrer"
                  >
                    press release
                  </a>
                  .
                </p>
              )}
            </>
          ) : (
            <>
              <p>{action.summary}</p>
              {action.press_release && (
                <p>
                  For more information, read the{" "}
                  <a
                    href={action.press_release}
                    target="_blank"
                    rel="noreferrer"
                  >
                    press release
                  </a>
                  .
                </p>
              )}
            </>
          ))}
      </div>
    </div>
  )
}
